import { EllipsisVertical } from 'lucide-react'
import { ReactNode, useState } from 'react'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

export type DropdownSection = {
  type: 'radio' | 'action'
  label: string
  items: DropdownItem[]
}

export type DropdownItem = {
  key: string
  label: string
  value?: string | number
  icon?: ReactNode
  onClick?: () => void
}

export type RadioSection = DropdownSection & {
  type: 'radio'
  value: string | number
  onValueChange: (value: string | number) => void
}

export type ActionSection = DropdownSection & {
  type: 'action'
}

type Props = {
  sections: (RadioSection | ActionSection)[]
  triggerIcon?: ReactNode
  triggerSize?: number
  /** メニュー本体にあてるクラス。react-modal-sheet の上に出すときの z-index 引き上げ用 */
  contentClassName?: string
}

export const CustomDropdown = ({ sections, triggerIcon, triggerSize = 40, contentClassName }: Props) => {
  const [open, setOpen] = useState(false)

  const displayTriggerIcon = triggerIcon || <EllipsisVertical size={triggerSize} />
  return (
    <DropdownMenu
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen)
      }}
    >
      <DropdownMenuTrigger asChild>
        <div onClick={() => setOpen(true)}>{displayTriggerIcon}</div>
      </DropdownMenuTrigger>
      {/*
        Header は常に画面最上部にあるため、上向きフリップすると画面外に出て使えなくなる。
        avoidCollisions を切って下向きに固定し、高さは available-height (max-h) で
        クランプして内部スクロールさせる。align="end" で右端のはみ出しも防ぐ。
      */}
      <DropdownMenuContent
        side="bottom"
        align="end"
        avoidCollisions={false}
        collisionPadding={8}
        className={contentClassName}
      >
        {sections.map((section, sectionIndex) => (
          <div key={section.label}>
            <DropdownMenuLabel className="text-gray-400">{section.label}</DropdownMenuLabel>
            {section.type === 'radio' ? (
              <DropdownMenuRadioGroup
                value={String(section.value)}
                onValueChange={(value) => section.onValueChange(value)}
              >
                {section.items.map((item) => (
                  <DropdownMenuRadioItem key={item.key} value={String(item.value)}>
                    {item.label}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            ) : (
              section.items.map((item) => (
                <DropdownMenuItem
                  key={item.key}
                  onClick={() => {
                    setOpen(false)
                    item.onClick?.()
                  }}
                  className="ms-2"
                >
                  {item.icon && <span className="mr-2">{item.icon}</span>}
                  {item.label}
                </DropdownMenuItem>
              ))
            )}
            {sectionIndex < sections.length - 1 && <DropdownMenuSeparator />}
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
